import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiErrorDetail } from "../lib/api";
import { Sparkles, TrendingUp, AlertTriangle, MessageCircle, PauseCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast, Toaster } from "sonner";

export default function Loyalty() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [outbound, setOutbound] = useState([]);

  function load() {
    api.get("/admin/loyalty-rebates").then((r) => setData(r.data));
    api.get("/admin/whatsapp/outbound").then((r) => setOutbound(r.data.items || []));
  }
  useEffect(load, []);

  async function resume(id) {
    try {
      await api.patch(`/admin/loyalty/${id}/resume`);
      toast.success("Loyalty resumed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  const summary = data?.summary;
  const eligible = data?.eligible_merchants || [];
  const granted = data?.rebates_granted || [];
  const paused = data?.paused_merchants || [];
  const budget = data?.budget;
  const budgetBreached = budget && budget.utilisation_pct >= 100;

  const rebateNotifs = outbound.filter((o) => o.meta?.kind === "rebate_granted");
  const budgetAlerts = outbound.filter((o) => o.meta?.kind === "rebate_budget_breached");

  return (
    <div className="p-8 space-y-6" data-testid="loyalty-page">
      <Toaster position="top-right" />
      <header>
        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">{t("admin_pages.loyalty_section")}</div>
        <h1 className="font-heading text-3xl font-bold mt-1 flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-amber-500" /> {t("admin_pages.loyalty_title")}
        </h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-xl">
          {t("admin_pages.loyalty_subtitle")}
        </p>
      </header>

      {/* Budget */}
      {budget && (
        <div className={`bg-white border rounded-md p-5 ${budgetBreached ? "border-destructive" : "border-border"}`} data-testid="budget-card">
          <div className="flex items-center justify-between mb-2">
            <div className="font-heading text-base font-semibold flex items-center gap-2">
              {budgetBreached && <AlertTriangle className="w-4 h-4 text-destructive" />}
              Monthly Rebate Budget
            </div>
            <div className="text-xs font-mono text-muted-foreground">
              KES {budget.used_this_month_kes.toLocaleString()} / {budget.monthly_budget_kes.toLocaleString()}
            </div>
          </div>
          <div className="w-full h-2 bg-stone-100 rounded overflow-hidden">
            <div
              className={`h-full transition-all ${budgetBreached ? "bg-destructive" : budget.utilisation_pct > 75 ? "bg-amber-500" : "bg-emerald-700"}`}
              style={{ width: `${Math.min(100, budget.utilisation_pct)}%` }}
            />
          </div>
          <div className="text-xs text-muted-foreground mt-2">
            {budgetBreached
              ? "Cap reached — new rebates auto-paused until next calendar month."
              : `KES ${budget.remaining_kes.toLocaleString()} remaining (${(100 - budget.utilisation_pct).toFixed(1)}%)`}
          </div>
        </div>
      )}

      {budgetAlerts.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-4 flex items-start gap-3" data-testid="budget-alert">
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
          <div className="text-sm text-amber-900">
            <div className="font-semibold">Finance officer notified</div>
            <div className="mt-0.5">{budgetAlerts[0].body}</div>
          </div>
        </div>
      )}

      {/* Summary cards */}
      <section className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="metric-card" data-testid="metric-eligible-count">
          <div className="text-xs uppercase tracking-[0.18em] text-stone-500 font-semibold">Eligible</div>
          <div className="mt-3 text-3xl font-bold font-mono">{summary?.eligible_merchants_count ?? "—"}</div>
          <div className="text-xs text-muted-foreground mt-1">Ready next cycle</div>
        </div>
        <div className="metric-card" data-testid="metric-paused-count">
          <div className="text-xs uppercase tracking-[0.18em] text-stone-500 font-semibold">Paused</div>
          <div className="mt-3 text-3xl font-bold font-mono">{summary?.paused_merchants_count ?? "—"}</div>
          <div className="text-xs text-muted-foreground mt-1">SIU/escalation</div>
        </div>
        <div className="metric-card" data-testid="metric-rebates-granted">
          <div className="text-xs uppercase tracking-[0.18em] text-stone-500 font-semibold">Granted</div>
          <div className="mt-3 text-3xl font-bold font-mono">{summary?.total_rebates_granted ?? "—"}</div>
          <div className="text-xs text-muted-foreground mt-1">All-time</div>
        </div>
        <div className="metric-card" data-testid="metric-rebate-amount">
          <div className="text-xs uppercase tracking-[0.18em] text-stone-500 font-semibold">Total Rebated</div>
          <div className="mt-3 text-3xl font-bold font-mono">KES {(summary?.total_amount_rebated_kes ?? 0).toLocaleString()}</div>
          <div className="text-xs text-muted-foreground mt-1">Discount given</div>
        </div>
      </section>

      {/* Eligible */}
      <div className="bg-white border border-border rounded-md" data-testid="eligible-card">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <div className="font-heading text-base font-semibold flex items-center gap-2">
            <TrendingUp className="w-4 h-4" /> Currently Eligible
          </div>
          <div className="text-xs text-muted-foreground">{eligible.length} merchants</div>
        </div>
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-stone-500">
            <tr>
              <th className="text-left font-medium py-3 px-5">Business</th>
              <th className="text-left font-medium py-3 px-5">Score</th>
              <th className="text-left font-medium py-3 px-5">Last 4</th>
            </tr>
          </thead>
          <tbody>
            {eligible.map((m) => (
              <tr key={m.merchant_id} className="border-t border-border table-row-hover" data-testid={`eligible-row-${m.merchant_id}`}>
                <td className="py-3 px-5"><div className="font-medium">{m.business_name}</div></td>
                <td className="py-3 px-5 font-mono"><span className="badge-status badge-success">{m.current_score}</span></td>
                <td className="py-3 px-5">
                  <div className="flex gap-1.5">
                    {m.last_scores.map((s, i) => (
                      <span key={i} className="font-mono text-xs px-2 py-0.5 rounded-sm bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200">{s}</span>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
            {eligible.length === 0 && <tr><td colSpan={3} className="py-10 text-center text-muted-foreground">No merchants eligible yet</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Paused */}
      {paused.length > 0 && (
        <div className="bg-white border border-border rounded-md" data-testid="paused-card">
          <div className="px-5 py-4 border-b border-border flex items-center gap-2">
            <PauseCircle className="w-4 h-4 text-destructive" />
            <div className="font-heading text-base font-semibold">Paused (SIU / Escalated)</div>
          </div>
          <table className="w-full text-sm">
            <tbody>
              {paused.map((m) => (
                <tr key={m.id} className="border-t border-border" data-testid={`paused-row-${m.id}`}>
                  <td className="py-3 px-5 font-medium">{m.business_name}</td>
                  <td className="py-3 px-5 text-xs text-muted-foreground">{m.loyalty_paused_reason}</td>
                  <td className="py-3 px-5 text-right">
                    <Button size="sm" variant="outline" onClick={() => resume(m.id)} data-testid={`resume-btn-${m.id}`}>
                      Resume
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Recent WhatsApp notifications */}
      {rebateNotifs.length > 0 && (
        <div className="bg-white border border-border rounded-md" data-testid="notifications-card">
          <div className="px-5 py-4 border-b border-border flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-emerald-700" />
            <div className="font-heading text-base font-semibold">Recent WhatsApp notifications</div>
          </div>
          <ul className="divide-y divide-border">
            {rebateNotifs.slice(0, 5).map((n) => (
              <li key={n.id} className="px-5 py-3 text-sm" data-testid={`notif-${n.id}`}>
                <div className="text-xs text-muted-foreground font-mono">{n.recipient} · {new Date(n.created_at).toLocaleString()}</div>
                <div className="mt-1 text-stone-700">{n.body}</div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Rebate history */}
      <div className="bg-white border border-border rounded-md" data-testid="rebates-card">
        <div className="px-5 py-4 border-b border-border font-heading text-base font-semibold">Rebate History</div>
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-stone-500">
            <tr>
              <th className="text-left font-medium py-3 px-5">Applied</th>
              <th className="text-left font-medium py-3 px-5">Merchant</th>
              <th className="text-left font-medium py-3 px-5">Original</th>
              <th className="text-left font-medium py-3 px-5">Rebate</th>
              <th className="text-left font-medium py-3 px-5">Final</th>
            </tr>
          </thead>
          <tbody>
            {granted.map((r) => (
              <tr key={r.id} className="border-t border-border" data-testid={`rebate-row-${r.id}`}>
                <td className="py-3 px-5 text-xs text-muted-foreground font-mono">{new Date(r.applied_at).toLocaleString()}</td>
                <td className="py-3 px-5 font-mono text-xs">{r.merchant_id.slice(0, 8)}…</td>
                <td className="py-3 px-5 font-mono">KES {r.original_amount}</td>
                <td className="py-3 px-5 font-mono text-emerald-700">−KES {r.rebate_amount}</td>
                <td className="py-3 px-5 font-mono font-semibold">KES {r.final_amount}</td>
              </tr>
            ))}
            {granted.length === 0 && <tr><td colSpan={5} className="py-10 text-center text-muted-foreground">No rebates granted yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
